import React from 'react';
import styled from '@emotion/styled/macro';
import { theme } from '@arnold/common';

const ScaleChoicesContainer = styled.div<{ columns: number; columnsMobile: number }>`
  width: 100%;
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: 8px;
  padding: 4px;
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns}, minmax(70px, 1fr));
  justify-items: center;
  align-items: start;
  background-color: ${theme.colors.backgroundBasic.default};

  @media (max-width: ${theme.breakpoint.small}) {
    grid-template-columns: repeat(${({ columnsMobile }) => columnsMobile}, minmax(63px, 1fr));
  }
`;

const ChoiceItem = styled.div<{ shiftToMiddleDesktop: boolean; shiftToMiddleMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  padding: 8px 0;
  margin-right: ${({ shiftToMiddleDesktop }) => (shiftToMiddleDesktop ? '-100%' : '0')};
  @media (max-width: ${theme.breakpoint.small}) {
    margin-right: ${({ shiftToMiddleMobile }) => (shiftToMiddleMobile ? '-100%' : '0')};
  }
`;

const ChoiceLabel = styled.div`
  margin-top: 2px;
  margin-bottom: 4px;
  font-size: 12px;
  color: #0d0a0b;
  font-weight: 400;
  padding: 0 4px;
  user-select: none;
`;

interface ScaleChoicesBoxProps {
  choices: string[];
  escapeOptionIndex?: number | null;
  selectedChoice: number | null | undefined;
  onChange: (index: number) => void;
}

const SCALE_CHOICE_COLUMNS_DESKTOP_MAX = 10;
const SCALE_CHOICE_COLUMNS_MOBILE_MAX = 5;

const ScaleChoicesBox: React.FC<ScaleChoicesBoxProps> = ({ choices, escapeOptionIndex, selectedChoice, onChange }) => {
  const handleChange = (index: number) => {
    if (escapeOptionIndex != null && index === escapeOptionIndex) {
      index = -1;
    }
    onChange?.(index);
  };

  let columnsDesktop = Math.min(SCALE_CHOICE_COLUMNS_DESKTOP_MAX, choices.length);
  const desktopRowCount = Math.ceil(choices.length / columnsDesktop);
  columnsDesktop = Math.ceil(choices.length / desktopRowCount);
  let columnsMobile = Math.min(SCALE_CHOICE_COLUMNS_MOBILE_MAX, choices.length);
  const mobileRowCount = Math.ceil(choices.length / columnsMobile);
  columnsMobile = Math.ceil(choices.length / mobileRowCount);
  const desktopIndexOfFirstItemInTheLastRow = desktopRowCount * columnsDesktop - columnsDesktop;
  const desktopLastRowHasLessItemsThanColumns = choices.length % columnsDesktop !== 0;
  const mobileIndexOfFirstItemInTheLastRow = mobileRowCount * columnsMobile - columnsMobile;
  const mobileLastRowHasLessItemsThanColumns = choices.length % columnsMobile !== 0;
  return (
    <ScaleChoicesContainer columns={columnsDesktop} columnsMobile={columnsMobile}>
      {choices.map((choice: string, index: number) => (
        <ChoiceItem
          key={`scale-choice-${index}`}
          onClick={() => handleChange(index)}
          shiftToMiddleDesktop={index >= desktopIndexOfFirstItemInTheLastRow && desktopLastRowHasLessItemsThanColumns}
          shiftToMiddleMobile={index >= mobileIndexOfFirstItemInTheLastRow && mobileLastRowHasLessItemsThanColumns}
        >
          <PrettyRadioButton
            checked={selectedChoice === index || (escapeOptionIndex === index && selectedChoice === -1)}
          />
          <ChoiceLabel>{choice}</ChoiceLabel>
        </ChoiceItem>
      ))}
    </ScaleChoicesContainer>
  );
};

export default ScaleChoicesBox;

interface PrettyRadioButtonProps {
  checked: boolean;
}

const PrettyRadioButton = ({ checked }: PrettyRadioButtonProps) => {
  return (
    <svg width="16px" height="16px" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="11px" cy="11px" r="10px" fill="white" stroke={theme.colors.actionPrimary.default} strokeWidth="2" />
      <MiddleDot cx="11px" fill={theme.colors.actionPrimary.default} checked={checked} />
    </svg>
  );
};

const MiddleDot = styled.circle<{ checked: boolean }>`
  r: ${({ checked }) => (checked ? '5.5px' : '4px')};
  cy: ${({ checked }) => (checked ? '11px' : '-4px')};
  transition:
    r 0.25s cubic-bezier(0.4, 0, 0.7, 4.1) 0.25s,
    cy 0.25s linear;
`;
